
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, ChevronUp, ChevronDown, Mic, MicOff, Download, Trash2, Wifi, WifiOff } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CallStatusCard } from '@/components/dialer/CallStatusCard';
import { LiveTranscript } from '@/components/dialer/LiveTranscript';
import { useTwilioContext } from '@/context/TwilioContext';

export const CallStatusSection = () => {
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  
  const { 
    callInProgress, 
    callStatus,
    endCall, 
    toggleMute,
    isMuted,
    transcript,
    isListening,
    startTranscription,
    stopTranscription,
    clearTranscript,
    downloadTranscript,
    backendConnected
  } = useTwilioContext();

  const hasCallData = callStatus !== 'idle';

  const handleEndCall = () => {
    console.log('🔴 CallStatusSection: Ending call');
    endCall();
  };

  const handleMuteToggle = () => {
    console.log('🔇 CallStatusSection: Toggling mute from', isMuted, 'to', !isMuted);
    toggleMute(!isMuted);
  };

  return (
    <>
      {/* Call Status */}
      <div className={`${hasCallData ? 'border-b bg-muted/30' : ''} transition-all duration-300`}>
        <div className="p-3 md:p-4">
          <CallStatusCard
            onEndCall={handleEndCall}
            onMuteToggle={handleMuteToggle}
          />
        </div>
      </div>

      {/* Live Transcript - show when there's call data */}
      <Collapsible 
        open={true}
        onOpenChange={setIsTranscriptExpanded}
        className="flex-1 flex flex-col border border-border/50 rounded-lg bg-card/30 min-h-0"
      >
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-between p-3 md:p-4 border-b border-border/30 shrink-0 hover:bg-muted/50 rounded-t-lg"
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="font-medium text-sm md:text-base">Live Transcript</span>
              {hasCallData && (
                <Badge 
                  variant={backendConnected ? "default" : "destructive"} 
                  className="text-xs"
                >
                  {backendConnected ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
                  Backend
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={isListening ? "destructive" : "default"}
                size="icon"
                onClick={isListening ? stopTranscription : startTranscription}
                className="h-8 w-8"
              >
                {isListening ? (
                  <MicOff className="h-3 w-3" />
                ) : (
                  <Mic className="h-3 w-3" />
                )}
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={downloadTranscript}
                disabled={transcript.length === 0}
                className="h-8 w-8"
              >
                <Download className="h-3 w-3" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={clearTranscript}
                disabled={transcript.length === 0}
                className="h-8 w-8"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
              
              <ChevronUp className="h-4 w-4" />
            </div>
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="flex-1 flex flex-col min-h-0">
          <LiveTranscript 
            isActive={true}
            onToggle={(active) => {
              console.log('LiveTranscript toggle:', active);
            }}
          />
        </CollapsibleContent>
      </Collapsible>

    </>
  );
};
